import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { Dialog } from 'primeng/dialog';
import { SkeletonModule } from 'primeng/skeleton';
import { Tooltip } from 'primeng/tooltip';
import { ApiService, GenericResponseCode, GenericResponseResult } from '@brighthub/api';
import { ClipboardService } from '@brighthub/utils';
import type { HouseholdMemberRow, HouseholdMembersResponse } from '../member-metadata-header.model';
import { HouseholdMemberProfileModalComponent } from '../household-member-profile-modal/household-member-profile-modal.component';

/**
 * Household members modal (MW-10562 Phase 4): roster fetched lazily from
 * `GET /v1/prototype/client/household` on every open (no cache — EDD OQ-8). The row matching the
 * active session guid is highlighted. Non-primary rows open a stacked member profile dialog
 * (OQ-11); primary is not clickable. Never switches the session. Fetch failures toast via
 * {@link MessageService} (HTTP 500 left to ApiService); the modal stays open with an empty body.
 */
@Component({
  selector: 'bn-household-members-modal',
  imports: [CommonModule, Dialog, SkeletonModule, Tooltip, HouseholdMemberProfileModalComponent],
  templateUrl: './household-members-modal.component.html',
  styleUrl: './household-members-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HouseholdMembersModalComponent {
  private readonly _api = inject(ApiService);
  private readonly _messageService = inject(MessageService);
  private readonly _clipboardService = inject(ClipboardService);
  /** Ignores stale responses when the dialog is reopened quickly. */
  private _fetchGeneration = 0;

  /** Guid used for the household lookup (the header payload guid). */
  readonly guid = input.required<string>();
  /** Active session guid — its row is highlighted. */
  readonly activeSessionGuid = input<string | null>(null);
  /** Two-way dialog visibility; opening triggers a fresh fetch. */
  readonly visible = model(false);

  readonly members = signal<HouseholdMemberRow[]>([]);
  readonly loading = signal(false);

  /** Member selected for the stacked profile dialog (non-primary only). */
  readonly profileMember = signal<HouseholdMemberRow | null>(null);
  readonly profileVisible = signal(false);

  readonly hasMembers = computed(() => this.members().length > 0);

  /** Subtitle under the dialog title, e.g. `3 members on file`. */
  readonly membersOnFileLabel = computed(() => {
    const n = this.members().length;
    return `${n} member${n === 1 ? '' : 's'} on file`;
  });

  constructor() {
    effect(() => {
      if (this.visible()) {
        this._fetch(this.guid());
      } else {
        this.closeMemberProfile();
      }
    });
  }

  isActiveSessionMember(member: HouseholdMemberRow): boolean {
    const active = this.activeSessionGuid();
    return !!active && member.guid === active;
  }

  /** True when the row may open the member profile dialog (not the primary). */
  isProfileClickable(member: HouseholdMemberRow): boolean {
    return this.relationshipLabel(member) === 'HOUSEHOLD';
  }

  /**
   * Relationship pill from schema role (`householdRole`): PRIMARY or HOUSEHOLD.
   * If the API omits `householdRole`, derive from null `relationship` (primary) vs present (household).
   */
  relationshipLabel(member: HouseholdMemberRow): string {
    if (member.householdRole === 'primary') {
      return 'PRIMARY';
    }
    if (member.householdRole === 'household') {
      return 'HOUSEHOLD';
    }
    // Older payloads without householdRole: primary row has null relationship.
    return member.relationship?.trim() ? 'HOUSEHOLD' : 'PRIMARY';
  }

  /** DOB display as `DOB MM/DD/YY`, or em dash when missing/invalid. */
  dobDisplay(member: HouseholdMemberRow): string {
    const dob = member.dateOfBirth;
    if (!dob) {
      return '—';
    }
    const [year, month, day] = dob.split('-').map(Number);
    if (!year || !month || !day) {
      return '—';
    }
    const mm = String(month).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    const yy = String(year % 100).padStart(2, '0');
    return `DOB ${mm}/${dd}/${yy}`;
  }

  /**
   * Copy member GUID (same {@link ClipboardService} path as the metadata header).
   * Stops propagation so the copy control does not open the member profile dialog.
   */
  copyGuid(event: Event, guid: string): void {
    event.preventDefault();
    event.stopPropagation();
    if (guid?.trim()) {
      this._clipboardService.copy(guid.trim());
    }
  }

  openMemberProfile(member: HouseholdMemberRow): void {
    if (!this.isProfileClickable(member)) {
      return;
    }
    this.profileMember.set(member);
    this.profileVisible.set(true);
  }

  closeMemberProfile(): void {
    this.profileVisible.set(false);
    this.profileMember.set(null);
  }

  onProfileVisibleChange(open: boolean): void {
    this.profileVisible.set(open);
    if (!open) {
      this.profileMember.set(null);
    }
  }

  close(): void {
    this.closeMemberProfile();
    this.visible.set(false);
  }

  private _fetch(guid: string): void {
    const generation = ++this._fetchGeneration;
    this.members.set([]);
    this.loading.set(true);
    const path = `/v1/prototype/client/household?guid=${encodeURIComponent(guid)}`;
    this._api.get<HouseholdMembersResponse>('brightnav-api', path).subscribe({
      next: (res: HouseholdMembersResponse & GenericResponseResult) => {
        if (generation !== this._fetchGeneration) {
          return;
        }
        this.loading.set(false);
        if (res?.result?.code !== GenericResponseCode.SERVICE_OK || !res.payload?.members) {
          this._toastFetchError(res?.result ?? new Error('Empty household response'));
          return;
        }
        this.members.set(res.payload.members);
      },
      error: (err: unknown) => {
        if (generation !== this._fetchGeneration) {
          return;
        }
        this.loading.set(false);
        this._toastFetchError(err);
      },
    });
  }

  /**
   * BrightNav toast for household fetch failures. Skips HTTP 500 so ApiService's generic toast
   * is not duplicated; modal stays open with an empty member list.
   */
  private _toastFetchError(err: unknown): void {
    if (this._isHttpStatus(err, 500)) {
      return;
    }
    const apiMessage = this._extractErrorMessage(err);
    this._messageService.add({
      severity: 'error',
      summary: 'Household members',
      detail: apiMessage || 'Unable to load household members.',
      life: 8000,
    });
  }

  /** True only for HTTP transport errors — envelope `result.status` is not ApiService's 500 toast path. */
  private _isHttpStatus(err: unknown, status: number): boolean {
    return err instanceof HttpErrorResponse && err.status === status;
  }

  private _extractErrorMessage(err: unknown): string | null {
    if (!err || typeof err !== 'object') {
      return null;
    }
    const http = err as HttpErrorResponse & {
      error?: { result?: { message?: string }; message?: string };
      message?: string;
      result?: { message?: string };
    };
    const candidates = [
      http.error?.result?.message,
      http.error?.message,
      http.result?.message,
      err instanceof Error ? err.message : null,
      typeof http.message === 'string' ? http.message : null,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }
    }
    return null;
  }
}
